import { memo, useCallback, useMemo, useState } from "react"
import { Box, Typography, Button, Card, CardContent, CardActions, Divider, Stack, Skeleton, Fab, SwipeableDrawer } from "@mui/material"
import { Email, WhatsApp, Download as DownloadIcon, ArrowForward } from "@mui/icons-material"
import { downloadBEList } from "../../utils"
import CopyLinkButton from "./CopyLinkButton"

import { calculateSelectionTotals } from "../utils/aggregation"
import { INQUIRY_CONFIG, generateInquiryBody } from "../utils/inquiry"

function InquirySection({ filteredData, selectedData, selections, totalCount, loading, isMobile, ctBounds }) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const hasSelection = selectedData.length > 0
    const dataToSummarize = useMemo(() => (
        hasSelection ? selectedData : filteredData
    ), [filteredData, hasSelection, selectedData])
    const { totalWeight, formattedAmount } = useMemo(() => (
        calculateSelectionTotals(dataToSummarize)
    ), [dataToSummarize])

    const handleEmailClick = useCallback(() => {
        const bodyText = generateInquiryBody(selectedData, selections, window.location.href, ctBounds)
        const subject = encodeURIComponent(INQUIRY_CONFIG.SUBJECT)
        const body = encodeURIComponent(bodyText)
        window.location.href = `mailto:${INQUIRY_CONFIG.EMAIL}?subject=${subject}&body=${body}`
    }, [selectedData, selections, ctBounds])

    const handleWhatsAppClick = useCallback(() => {
        const bodyText = generateInquiryBody(selectedData, selections, window.location.href, ctBounds)
        const text = encodeURIComponent(bodyText)
        window.location.href = `https://wa.me/${INQUIRY_CONFIG.WHATSAPP_PHONE}?text=${text}`
    }, [selectedData, selections, ctBounds])

    const handleDownloadClick = useCallback(() => {
        downloadBEList(dataToSummarize)
    }, [dataToSummarize])

    const inquiryContent = (
        <>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                {loading ? (
                    <CardContent sx={{ py: 3, px: 3 }}>
                        <Skeleton variant="rectangular" height={108} sx={{ borderRadius: 1 }} />
                    </CardContent>
                ) : (
                    <>
                        <CardContent sx={{ py: 3, px: 3 }}>
                            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', lineHeight: 1, letterSpacing: 1.2, mb: 1 }}>
                                {hasSelection ? 'Selected' : 'Viewing'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h4" component="span" sx={{ fontWeight: 800, color: hasSelection ? 'primary.main' : 'text.primary' }}>
                                    {hasSelection ? selectedData.length : filteredData.length}
                                </Typography>
                                <Typography variant="h6" component="span" sx={{ color: 'text.secondary', fontWeight: 500, opacity: 0.8 }}>
                                    / {totalCount}
                                </Typography>
                                <Typography variant="overline" component="span" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5, letterSpacing: 1.2 }}>
                                    Stones
                                </Typography>
                            </Box>
                        </CardContent>

                        {hasSelection && (
                            <>
                                <Divider />
                                <CardContent sx={{ py: 2, px: 3 }}>
                                    <Stack spacing={1.5}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">Total Weight</Typography>
                                            <Typography variant="body2" fontWeight="600">{totalWeight.toFixed(2)} ct</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                                            <Typography variant="body2" fontWeight="600">{formattedAmount}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </>
                        )}
                    </>
                )}

                <Divider />

                <CardActions sx={{ p: 0.5, gap: 0.5 }}>
                    <CopyLinkButton />
                    <Button
                        variant="iconAction"
                        color="inherit"
                        onClick={handleEmailClick}
                    >
                        <Email fontSize="small" />
                    </Button>
                    <Button
                        variant="iconAction"
                        color="inherit"
                        onClick={handleWhatsAppClick}
                    >
                        <WhatsApp fontSize="small" />
                    </Button>
                </CardActions>
            </Card>

            <Button
                fullWidth
                variant="downloadAction"
                color="inherit"
                startIcon={<DownloadIcon />}
                sx={{ mt: 1, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                onClick={handleDownloadClick}
            >
                Download List
            </Button>
        </>
    )

    if (isMobile) {
        return (
            <>
                <Fab 
                    variant="extended" 
                    color="primary" 
                    sx={{ position: 'fixed', bottom: 24, right: 24, fontWeight: 700, px: 3, boxShadow: 4 }}
                    onClick={() => setIsDrawerOpen(true)}
                >
                    INQUIRY <ArrowForward sx={{ ml: 1 }} />
                </Fab>
                <SwipeableDrawer
                    anchor="bottom"
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    onOpen={() => setIsDrawerOpen(true)}
                    disableSwipeToOpen
                    PaperProps={{
                        sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 3, pb: 4 }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Make inquiry
                    </Typography>
                    {inquiryContent}
                </SwipeableDrawer>
            </>
        )
    }

    return (
        <Box sx={{ gridColumn: '2', display: 'flex', flexDirection: 'column', mt: 1 }}>
            {inquiryContent}
        </Box>
    )
}

export default memo(InquirySection)
